import type { GuestTypeDefaultsMap } from "@/lib/guestType";
import type { OccupancySettings } from "@/lib/occupancySettings";
import type { TouristPaymentMoment } from "@/lib/touristPayment";

export type ElectricityBillingMode = "flat_per_night" | "per_kwh" | "flat_with_included_kwh";

export type DepositMode = "percent" | "fixed" | "first_night";

export interface DepositSettings {
  mode: DepositMode;
  percent: string;
  fixed_amount: string | null;
  cancellation_deadline_days: number;
  cancellation_deadline_time: string;
}

export interface OccupancyPriceBand {
  min_occupancy_percent: number;
  surcharge_percent: string;
}

export interface EarlyBookingBand {
  min_days_before_arrival: number;
  adjustment_percent: string;
}

export interface PricingPolicySettings {
  dynamic_enabled: boolean;
  dynamic_use_previous_year: boolean;
  dynamic_use_current_bookings: boolean;
  dynamic_bands: OccupancyPriceBand[];
  early_booking_enabled: boolean;
  early_booking_bands: EarlyBookingBand[];
}

export const DEFAULT_PRICING_POLICY: PricingPolicySettings = {
  dynamic_enabled: false,
  dynamic_use_previous_year: true,
  dynamic_use_current_bookings: true,
  dynamic_bands: [
    { min_occupancy_percent: 70, surcharge_percent: "10" },
    { min_occupancy_percent: 85, surcharge_percent: "20" },
    { min_occupancy_percent: 95, surcharge_percent: "35" },
  ],
  early_booking_enabled: false,
  early_booking_bands: [
    { min_days_before_arrival: 90, adjustment_percent: "-15" },
    { min_days_before_arrival: 30, adjustment_percent: "0" },
    { min_days_before_arrival: 0, adjustment_percent: "15" },
  ],
};

export interface PaymentTermsSettings {
  days: number;
  label: string | null;
}

export interface TenantSettings {
  electricity_billing: {
    mode: ElectricityBillingMode;
    included_kwh_per_night: string | null;
  };
  deposit: DepositSettings;
  pricing_policy?: PricingPolicySettings;
  payment_terms: PaymentTermsSettings;
  checkout: { requires_paid_invoice: boolean; departure_time?: string };
  occupancy?: OccupancySettings;
  tourist_payment: { moment: TouristPaymentMoment };
  guest_type_defaults: GuestTypeDefaultsMap;
}

/** Gemeinsame Props aller Abschnitte auf der Seite „Buchungs- & Zahlungsregeln“ */
export interface SettingsSectionProps {
  settings: TenantSettings;
  canWrite: boolean;
  /** Nach erfolgreichem Speichern: Einstellungen neu laden */
  onSaved: () => void;
}

export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}
